/*
 * <elexa-strip>: seamless marquee engine for the scrolling features bar, the scrolling ticker and the
 * "As seen in" logos (scrolling layout). Styles: assets/elexa-marquee.css (.ex-marquee*).
 * (The benefits carousel keeps its own <elexa-marquee> element in assets/elexa-marquee.js.)
 *
 * The server renders the item set plus copies (aria-hidden, inert). This script
 * - adds copies when the screen is wide, so (copies - 1) sets always cover twice the strip, and keeps
 *   --ex-mq-copies in sync (the CSS animation moves the track by 100% / copies = exactly one set);
 * - re-measures after fonts and images load, and on resize (debounced 250 ms);
 * - pauses while touched (resumes on release), while the tab is hidden and while one of its blocks is
 *   selected in the theme editor (hover and keyboard-focus pauses are CSS);
 * - inserts merchant SVG icons after sanitising them (SVG elements only; no scripts, event
 *   attributes, external links or foreignObject). Filled icons take the icon colour and lose their
 *   strokes; outline icons (fill="none" on the root) keep their strokes in the icon colour.
 */
(() => {
  if (customElements.get('elexa-strip')) return;

  const ALLOWED = new Set([
    'svg', 'g', 'path', 'circle', 'ellipse', 'line', 'polyline', 'polygon', 'rect', 'defs',
    'lineargradient', 'radialgradient', 'stop', 'clippath', 'mask', 'title', 'desc', 'symbol', 'use',
  ]);
  const SHAPES = new Set(['path', 'circle', 'ellipse', 'line', 'polyline', 'polygon', 'rect']);
  const cache = new Map();

  function clean(el) {
    Array.from(el.children).forEach((child) => {
      if (ALLOWED.has(child.nodeName.toLowerCase())) clean(child);
      else child.remove();
    });
    Array.from(el.attributes).forEach((attr) => {
      const name = attr.name.toLowerCase();
      const value = attr.value.replace(/\s+/g, '').toLowerCase();
      const isLink = name === 'href' || name.endsWith(':href');
      if (
        name.startsWith('on') ||
        (isLink && !value.startsWith('#')) ||
        (name === 'style' && /url\(|expression|javascript:/.test(value)) ||
        /javascript:/.test(value)
      ) {
        el.removeAttribute(attr.name);
      }
    });
  }

  function sanitizeSvg(code) {
    if (cache.has(code)) return cache.get(code);
    let result = null;
    try {
      // Strict SVG (XML) first; markup that is not well-formed XML is read by an inert HTML parser
      // (nothing in a DOMParser document runs or loads), then cleaned the same way.
      const source = String(code).trim();
      let svg = null;
      const xml = new DOMParser().parseFromString(source, 'image/svg+xml');
      if (!xml.getElementsByTagName('parsererror').length && xml.documentElement.nodeName.toLowerCase() === 'svg') {
        svg = xml.documentElement;
      } else if (/^<svg[\s>]/i.test(source)) {
        svg = new DOMParser().parseFromString(source, 'text/html').querySelector('svg');
      }
      if (svg) {
        clean(svg);
        const outline = (svg.getAttribute('fill') || '').toLowerCase() === 'none';
        if (outline) {
          svg.setAttribute('stroke', 'currentColor');
          svg.querySelectorAll('*').forEach((node) => {
            if (!SHAPES.has(node.nodeName.toLowerCase())) return;
            const stroke = (node.getAttribute('stroke') || '').toLowerCase();
            if (stroke && stroke !== 'none') node.setAttribute('stroke', 'currentColor');
          });
        } else {
          svg.setAttribute('fill', 'currentColor');
          svg.removeAttribute('stroke');
          svg.querySelectorAll('*').forEach((node) => {
            if (!SHAPES.has(node.nodeName.toLowerCase())) return;
            const fill = (node.getAttribute('fill') || '').toLowerCase();
            if (fill && fill !== 'none') node.setAttribute('fill', 'currentColor');
            node.removeAttribute('stroke');
            node.style.stroke = 'none';
          });
        }
        svg.removeAttribute('width');
        svg.removeAttribute('height');
        svg.setAttribute('aria-hidden', 'true');
        svg.setAttribute('focusable', 'false');
        result = new XMLSerializer().serializeToString(svg);
      }
    } catch (error) {
      result = null;
    }
    cache.set(code, result);
    return result;
  }

  class ElexaStrip extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.ex-marquee__track');
      if (!this.track || this.ready) return;
      this.ready = true;
      this.minCopies = parseInt(this.dataset.minCopies, 10) || 3;
      this.insertIcons();

      this.onResize = () => {
        clearTimeout(this.resizeTimer);
        this.resizeTimer = setTimeout(() => this.fill(), 250);
      };
      this.onVisibility = () => this.classList.toggle('is-hidden-tab', document.hidden);
      this.onTouchStart = (event) => {
        if (event.pointerType !== 'mouse') this.classList.add('is-paused');
      };
      this.onTouchEnd = () => this.classList.remove('is-paused');
      this.onBlockSelect = (event) => {
        if (event.target && this.contains(event.target)) this.classList.add('is-editor-paused');
      };
      this.onBlockDeselect = (event) => {
        if (event.target && this.contains(event.target)) this.classList.remove('is-editor-paused');
      };

      window.addEventListener('resize', this.onResize);
      document.addEventListener('visibilitychange', this.onVisibility);
      document.addEventListener('shopify:block:select', this.onBlockSelect);
      document.addEventListener('shopify:block:deselect', this.onBlockDeselect);
      this.addEventListener('pointerdown', this.onTouchStart);
      this.addEventListener('pointerup', this.onTouchEnd);
      this.addEventListener('pointercancel', this.onTouchEnd);
      this.addEventListener('pointerleave', this.onTouchEnd);
      this.onVisibility();
      this.fill();
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(() => this.fill());
      // Images (logos, image icons, image dividers) change the set width once they load.
      this.querySelectorAll('.ex-marquee__set:first-child img').forEach((img) => {
        if (!img.complete) img.addEventListener('load', this.onResize, { once: true });
      });
    }

    disconnectedCallback() {
      this.ready = false;
      window.removeEventListener('resize', this.onResize);
      document.removeEventListener('visibilitychange', this.onVisibility);
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
      clearTimeout(this.resizeTimer);
    }

    insertIcons() {
      this.querySelectorAll('[data-ex-svg]').forEach((source) => {
        const markup = sanitizeSvg(source.getAttribute('data-ex-svg'));
        const key = source.getAttribute('data-ex-svg-key');
        this.querySelectorAll(`[data-ex-svg-slot="${key}"]`).forEach((slot) => {
          if (markup) slot.innerHTML = markup;
          else slot.remove();
        });
        source.removeAttribute('data-ex-svg');
      });
    }

    sets() {
      return Array.from(this.track.children).filter((el) => el.classList.contains('ex-marquee__set'));
    }

    fill() {
      if (!this.isConnected) return;
      const sets = this.sets();
      const first = sets[0];
      if (!first) return;
      const setWidth = first.getBoundingClientRect().width;
      const viewWidth = this.clientWidth || window.innerWidth;
      if (!setWidth) return;
      const needed = Math.min(40, Math.max(this.minCopies, Math.ceil((viewWidth * 2) / setWidth) + 1));
      // Copy a server-rendered clone when there is one (it carries no editor block attributes).
      const model = sets.find((el) => el.hasAttribute('data-ex-clone')) || first;
      let count = sets.length;
      while (count < needed) {
        const copy = model.cloneNode(true);
        copy.setAttribute('aria-hidden', 'true');
        copy.setAttribute('data-ex-clone', '');
        copy.removeAttribute('role');
        copy.inert = true;
        copy.querySelectorAll('[id]').forEach((el) => el.removeAttribute('id'));
        copy.querySelectorAll('[data-shopify-editor-block]').forEach((el) => el.removeAttribute('data-shopify-editor-block'));
        copy.querySelectorAll('img[loading="lazy"]').forEach((img) => img.setAttribute('loading', 'eager'));
        this.track.appendChild(copy);
        count += 1;
      }
      // One set is always 100% / copies of the track, so updating the count never moves the strip.
      const current = parseInt(getComputedStyle(this).getPropertyValue('--ex-mq-copies'), 10);
      if (current !== count) this.style.setProperty('--ex-mq-copies', String(count));
    }
  }

  customElements.define('elexa-strip', ElexaStrip);
})();
